import { useTranslation } from 'next-i18next';
import type { IbdRecommendationData } from '@hub/free-form-hub/common/recommendations/ibd-recommendation/useIbdRecommendation';
import { WidenImage } from '@hub/common/Widen/WidenImage';
import { formatPrice } from '@hub/libs/product-price-utils';
import type { Session } from '@hub/hooks/states/useSessionStore';
import { useRouter } from 'next/router';
import { getLocale } from '@hub/libs/constants';

type IbdRecommendationTileProps = {
  data: IbdRecommendationData;
  fileDimensions?: Record<string, number>;
  quantity: number;
  session: Session;
};
export const IbdRecommendationTile = ({ data, fileDimensions, quantity, session }: IbdRecommendationTileProps) => {
  const { t } = useTranslation();
  const { articleName, exactCatalogMatch, article, articlesCount, shape } = data;
  const router = useRouter();
  const locale = getLocale(router);

  const shopUrl = () => {
    const url = new URL(article?.shopUrl);
    const parameters = new URLSearchParams(url.search);
    parameters.set('utm_source', 'igusdeshop');
    parameters.set('utm_media', 'cadiframeclick');
    parameters.set('utm_campaign', 'hmishopclick_shop');
    url.search = parameters.toString();
    return url.toString();
  };

  const shapeNameSection = (
    <div>
      <h4 className="mb-6 text-2xl">{t(`COMMON.FILE_UPLOAD.TITLE`, { ns: 'free-form-hub/freeFormHubCommon' })}</h4>
      <div className="text-center font-medium text-2xl mt-4">
        <span>{t(`COMMON.FILE_UPLOAD.RECOGNIZED_PRODUCT`, { ns: 'free-form-hub/freeFormHubCommon' })}</span>
        &nbsp;
        <span>{t(`COMMON.BEARING_HUB.SHAPES.${shape}.HEADLINE`, { ns: 'free-form-hub/freeFormHubCommon' })}</span>
      </div>
    </div>
  );

  const dimensionsSection = (
    <div className="flex flex-row justify-center	mt-10">
      <div className="mr-10">
        <WidenImage
          shortCode={data.widen.schemaShortKey}
          title={t(`COMMON.BEARING_HUB.SHAPES.${shape}.HEADLINE`, { ns: 'free-form-hub/freeFormHubCommon' })}
          alt={t(`COMMON.BEARING_HUB.SHAPES.${shape}.HEADLINE`, { ns: 'free-form-hub/freeFormHubCommon' })}
          filename={data.widen.schemaShortKey}
          width={250}
          height={128}
        />
      </div>
      {fileDimensions && (
        <div className="flex flex-col">
          <div className="mt-2 mb-1.5">
            <h4 className="text-[15px]">
              {t(`COMMON.FILE_UPLOAD.DIMENSIONS`, { ns: 'free-form-hub/freeFormHubCommon' })}
            </h4>
          </div>
          <ul className="block">
            {Object.keys(fileDimensions).map(key => (
              <li className="text-xs" key={key}>
                <span className="font-bold">{key}:</span> {fileDimensions[key].toFixed(2)} mm
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );

  const productTile = (
    <div className="flex flex-col items-center m-h-[325px] w-[234px] text-center shadow-2xl p-[15px] rounded-lg border-0">
      <div className="text-sm font-semibold leading-7 max-h-[55px] overflow-hidden">
        {t(exactCatalogMatch ? 'COMMON.FILE_UPLOAD.IDENTICAL_PRODUCTS' : 'COMMON.FILE_UPLOAD.SIMILAR_PRODUCTS', {
          count: articlesCount,
          ns: 'free-form-hub/freeFormHubCommon',
        })}
      </div>
      <div className="my-[24px]">
        <WidenImage
          shortCode={data.article.widen}
          title={t(`COMMON.BEARING_HUB.SHAPES.${shape}.HEADLINE`, { ns: 'free-form-hub/freeFormHubCommon' })}
          alt={t(`COMMON.BEARING_HUB.SHAPES.${shape}.HEADLINE`, { ns: 'free-form-hub/freeFormHubCommon' })}
          filename={data.article.widen}
          width={214}
          height={82}
        />
      </div>
      <div className="text-xs font-normal text-gray-800 mt-2">{articleName}</div>
      {article.unitPrice.value > 0 ? (
        <>
          <div className="text-sm font-normal mt-4 whitespace-nowrap">
            {t(`COMMON.FILE_UPLOAD.FROM`, {
              ns: 'free-form-hub/freeFormHubCommon',
            })}
            <span className="text-orange-500 font-semibold">
              {' '}
              {formatPrice(article.unitPrice.value, locale, session.currency)}
            </span>
            {t(`COMMON.FILE_UPLOAD.PER_PIECE`, {
              ns: 'free-form-hub/freeFormHubCommon',
            })}
          </div>
          <div className="text-[10px] font-light text-gray-800">
            {t('COMMON.FILE_UPLOAD.BROWSE', {
              quantity,
              ns: 'free-form-hub/freeFormHubCommon',
            })}
          </div>
        </>
      ) : (
        <div className="text-sm font-normal mt-4 whitespace-nowrap">
          <span className="text-orange-500 font-semibold">
            {t(`COMMON.FILE_UPLOAD.BROWSE`, {
              ns: 'free-form-hub/freeFormHubCommon',
            })}
          </span>
        </div>
      )}
      <a
        id="cad-search-shop-link"
        className="min-w-[175px] mt-4 py-2 px-6 rounded-md border border-transparent text-base font-medium text-white bg-orange-500 hover:bg-orange-600"
        href={shopUrl()}
        target="_blank"
        rel="noreferrer"
      >
        {t('COMMON.FILE_UPLOAD.GO_TO_IBH', { ns: 'free-form-hub/freeFormHubCommon' })}
      </a>
      <div className="text-xs font-medium text-availability-fast pt-[10px]">
        {t(`COMMON.FILE_UPLOAD.READY_TO_SHIP_DAYS`, {
          time: article.deliveryTime.days > 0 ? article.deliveryTime.days : 1,
          ns: 'free-form-hub/freeFormHubCommon',
        })}
      </div>
    </div>
  );

  return (
    <div>
      {shapeNameSection}
      {dimensionsSection}
      <h5 className="text-[16px] mt-2.5 mb-[20px] font-medium">
        {t(`COMMON.FILE_UPLOAD.RECOMMENDATION`, { ns: 'free-form-hub/freeFormHubCommon' })}
      </h5>
      <div className="flex justify-center gap-[30px]">{productTile}</div>
    </div>
  );
};
